import { OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Component, DestroyRef, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { UtilsService } from '../../services/utils.service';
import { WalkthroughService } from '../../services/walkthrough.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { ObjectCreatorService } from '../../services/object-creator.service';

import { DataObject, Workspace } from '../../models/core.models';
import { NomObjectClass, SortDirection } from '../../constants';

import {
  setHomeTab,
  setHomeSortingField,
  setHomeFilterString,
  setHomeObjectsLifeCycle,
  setHomeSortingDirection,
} from '../../actions/home.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from '../../actions/selected.actons';
import { objectChanged } from '../../actions/core.actions';

import {
  selectCurrentCompanyId,
  selectSelectedObjects,
  selectUser,
  selectHomeContextSettings,
  selectHomeFilterString,
  selectHomeObjectsLifeCycle,
  selectHomeSortingField,
  selectHomeSortingDirection,
} from '../../reducers';
import { UserApiService } from 'src/app/api/queries/user-api.service';
import { AppState } from 'src/app/reducers';
import {
  UserWorkspacesScope,
  injectUserWorkspacesQuery,
} from 'src/app/api/queries/workspace.queries';
import {
  RecentObjectsScope,
  injectRecentObjectsQuery,
} from 'src/app/api/queries/user-api.queries';
import {
  UserViewOptionsScope,
  injectHomeViewOptionsQuery,
  injectToggleUserViewOption,
} from 'src/app/api/queries/view-preset.queries';
import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';
import { ViewId } from '@services/view-preset.service';

import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  take,
  tap,
} from 'rxjs';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ObjectContextSettings } from '../../services/object.service';
import { PropertyViewConfig } from '../../services/feed-settings.service';

export type HomeTab = 'active' | 'recent' | 'archived' | 'deleted';

/** A row in a projected toolbar dropdown panel (view options / sort). */
interface ToolbarMenuItem {
  text?: string;
  icon?: IconProp;
  value?: unknown;
  label?: string;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
}

/** A run of consecutive same-`radioGroup` rows, or a single toggle row. */
interface ToolbarMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: unknown;
  items: ToolbarMenuItem[];
}

/** A command row in the "apply to selected" dropdown (from ObjectActionsService). */
interface ApplyAction {
  text: string;
  icon?: IconProp;
  // eslint-disable-next-line @typescript-eslint/ban-types
  action?: Function; // matches ObjectActionsService.getButtonForAction's action
}
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomeComponent implements OnInit {
  currentTab: HomeTab;
  filterString: string;
  // `[]` rather than undefined: `workspaces$` is signal-backed and emits on the
  // first change-detection pass, not synchronously on subscribe.
  workspaces: Array<Workspace> = [];
  viewConfig: PropertyViewConfig[] = [];

  /** `hv-objects` — the view-options blob this screen renders and toggles. */
  private viewOptionsScope: Signal<UserViewOptionsScope | null>;
  private viewOptionsQuery: ReturnType<typeof injectHomeViewOptionsQuery>;
  private viewOptions$: Observable<PropertyViewConfig[]>;
  private readonly toggleViewOption = injectToggleUserViewOption(
    ViewId.HomeViewObjects,
  );
  private readonly userWorkspaces = inject(UserWorkspacesService);
  private readonly userApi = inject(UserApiService);

  // * Toolbar state (migrated from HomeActionsService)
  selectAllChecked = false;
  lifecycleStatus: number = 1;
  sortField = 'title';
  sortDirection = 'asc';
  viewItems: ToolbarMenuItem[] = [];
  viewGroups: ToolbarMenuGroup[] = [];
  applyActions: ApplyAction[] = [];
  applyCount = 0;

  private readonly statusLabels: Record<number, string> = {
    1: 'home-actions.button.active',
    3: 'home-actions.button.archived',
    4: 'home-actions.button.deleted',
  };
  private readonly statusIcons: Record<number, IconProp> = {
    1: ['fas', 'check'],
    3: ['fas', 'box-archive'],
    4: ['fas', 'trash'],
  };

  /** Sort trigger's leading icon reflects direction; the trailing icon reflects the field. */
  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'sort-amount-up']
      : ['fas', 'sort-amount-down'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortField === 'time_created' ||
      this.sortField === 'time_modified'
      ? ['fas', 'clock']
      : ['fas', 'text-height'];
  }
  get statusLabel(): string {
    return this.translateService.instant(
      this.statusLabels[this.lifecycleStatus] ?? this.statusLabels[1],
    );
  }
  get statusIcon(): IconProp {
    return this.statusIcons[this.lifecycleStatus] ?? this.statusIcons[1];
  }
  get applyLabel(): string {
    return `${this.translateService.instant('home-actions.button.apply-to-selected')} (${this.applyCount})`;
  }
  get sortLabel(): string {
    return this.translateService.instant('home-actions.button.sort');
  }
  get viewLabel(): string {
    return this.translateService.instant('workspace-actions.button.view');
  }

  isLoading = true;
  tabContent: 'workspaces' | 'recent' = 'workspaces';

  loading$: Observable<boolean>;
  workspaces$: Observable<Array<Workspace>>;
  recentObjects$: Observable<Array<DataObject>>;

  private destroyRef = inject(DestroyRef);

  constructor(
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected utilService: UtilsService,
    protected objectActionService: ObjectActionsService,
    protected objectCreatorService: ObjectCreatorService,
    protected translateService: TranslateService,
    protected walktrhoughService: WalkthroughService,
  ) {
    const user = this.store.selectSignal(selectUser);
    const companyId = this.store.selectSignal(selectCurrentCompanyId);
    const lifecycleStatus = this.store.selectSignal(selectHomeObjectsLifeCycle);

    this.viewOptionsScope = computed<UserViewOptionsScope | null>(() => {
      const currentUser = user();

      return currentUser?.id
        ? {
            userId: currentUser.id,
            companyId: companyId(),
            user: currentUser,
          }
        : null;
    });

    this.viewOptionsQuery = injectHomeViewOptionsQuery(this.viewOptionsScope);

    // `toObservable` has to be built here, in an injection context; `setupToolbar`
    // runs from `ngOnInit` and only subscribes.
    this.viewOptions$ = toObservable(
      computed(() => this.viewOptionsQuery.data() ?? []),
    );

    this.viewOptions$
      .pipe(
        tap((config) => (this.viewConfig = config)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // The list must not be fetched before the stats view option is known:
    // `withStats` is not a key term, so a first fetch without it would stick.
    // "No longer pending" covers success *and* failure, because the view-options
    // query degrades to defaults rather than writing nothing.
    const viewOptionsSettled = computed(
      () => !!this.viewOptionsScope() && !this.viewOptionsQuery.isPending(),
    );

    // Home owns the workspaces query and is its only reactive observer. The status
    // filter is a key term; the stats view option rides alongside it as a fetch
    // strategy.
    const scope = computed<UserWorkspacesScope | null>(() => {
      const userId = user()?.id;
      if (!userId || !viewOptionsSettled()) {
        return null;
      }

      return {
        userId,
        companyId: companyId(),
        lifecycleStatus: lifecycleStatus(),
        withStats:
          this.viewOptionsQuery.data()?.find((opt) => opt.path === 'stats')
            ?.selected ?? false,
      };
    });

    const workspacesQuery = injectUserWorkspacesQuery(scope);

    const filterString = this.store.selectSignal(selectHomeFilterString);
    const sortingField = this.store.selectSignal(selectHomeSortingField);
    const sortingDirection = this.store.selectSignal(
      selectHomeSortingDirection,
    );

    const visibleWorkspaces = computed<Workspace[]>(() => {
      const normalizedFilterString = (filterString() ?? '').toLowerCase();

      const objects = (workspacesQuery.data() ?? []).filter((obj) => {
        if (!normalizedFilterString) return true;

        // @FIXME: BE returns some null values
        const title = String(obj.title ?? '');
        const description = String(obj.description ?? '');

        return (
          title.toLowerCase().includes(normalizedFilterString) ||
          description.toLowerCase().includes(normalizedFilterString)
        );
      });

      const field = sortingField();
      if (field) {
        objects.sort((a, b) => {
          if (sortingDirection() === 'asc') {
            return a[field] > b[field] ? 1 : -1;
          } else {
            return a[field] < b[field] ? 1 : -1;
          }
        });
      }

      return objects;
    });

    this.workspaces$ = toObservable(visibleWorkspaces);

    this.workspaces$
      .pipe(
        // side-effect: update the local workspaces array
        tap((workspaces) => (this.workspaces = workspaces)),
        // automatically unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    // A second reactive observer alongside the header's `search-suggest` costs
    // nothing: the query never goes stale, so mounting home cannot refetch - which
    // is what keeps a client-side removal from coming back.
    const recentObjectsScope = computed<RecentObjectsScope | null>(() => {
      const userId = user()?.id;

      return userId ? { userId, companyId: companyId() } : null;
    });

    const recentObjectsQuery = injectRecentObjectsQuery(recentObjectsScope);

    this.recentObjects$ = toObservable(
      computed(() => recentObjectsQuery.data() ?? []),
    );
    this.loading$ = toObservable(
      computed(() => !viewOptionsSettled() || workspacesQuery.isLoading()),
    );
  }

  public ngOnInit(): void {
    this.isLoading = false;
    this.store.dispatch(objectChanged({ object: null }));

    this.route.params
      .pipe(
        tap((params) => {
          // Set default tab if none is provided
          this.currentTab = params['tab'] ? params['tab'] : 'active'; // Replace 'defaultTab' with your actual default tab value
          this.store.dispatch(setHomeTab({ tab: this.currentTab }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setupToolbar();
  }

  public setupToolbar(): void {
    this.store
      .pipe(
        select(selectHomeObjectsLifeCycle),
        distinctUntilChanged(),
        tap(
          (status) =>
            (this.lifecycleStatus = this.statusLabels[status] ? status : 1),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([
      this.store.pipe(
        select(selectHomeSortingDirection),
        distinctUntilChanged(),
      ),
      this.store.pipe(select(selectHomeSortingField), distinctUntilChanged()),
    ])
      .pipe(
        tap(([dir, field]) => {
          this.sortDirection = dir;
          this.sortField = field;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.viewOptions$
      .pipe(
        tap((config) => {
          this.viewItems = config.map((c) => ({
            text: c.label,
            label: c.label,
            icon: c.icon,
            value: c.path,
            isToggle: !c.isRadio,
            isRadio: c.isRadio,
            isSelected: c.selected,
            radioGroup: c.radioGroup,
          }));
          this.viewGroups = this.groupViewItems(this.viewItems);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectSelectedObjects),
        tap((selectedObjects) => {
          this.applyCount = selectedObjects.size;
          this.updateApplyActions(selectedObjects);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectHomeFilterString)
      .pipe(
        tap((filterString) => (this.filterString = filterString)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateApplyActions(selectedObjects: Map<string, DataObject>): void {
    let applicable: string[] = [];
    selectedObjects.forEach((object) => {
      let classId = object.object_class_id;
      const typeId = object.object_type_id || null;
      // widget placeholders set object_class_id/object_id to the same value
      if (object['widget_type_id'] !== undefined) {
        classId = NomObjectClass.WIDGET;
      }
      const actions =
        this.objectActionService.objectActions[classId]?.[
          object.lifecycle_status
        ]?.[typeId] ?? null;
      if (actions) {
        const enabled = Object.keys(actions).filter((a) => actions[a] === true);
        applicable =
          applicable.length === 0
            ? enabled
            : applicable.filter((a) => enabled.includes(a));
      }
    });

    this.applyActions = applicable.map((action) => {
      const button = this.objectActionService.getButtonForAction(action);
      return {
        text: button.label,
        icon: button.leadingIcon,
        action: button.action,
      };
    });
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.selectAllWorkspaces(selected);
  }

  onStatusChange(status: number): void {
    this.lifecycleStatus = status;
    this.store.dispatch(
      setHomeObjectsLifeCycle({ homeObjectsLifeCycle: status }),
    );
  }

  onSortFieldChange(sortingField: string): void {
    this.sortField = sortingField;
    this.store.dispatch(setHomeSortingField({ sortingField }));
    this.persistSorting();
  }

  onSortDirectionChange(sortingDirection: SortDirection): void {
    this.sortDirection = sortingDirection;
    this.store.dispatch(setHomeSortingDirection({ sortingDirection }));
    this.persistSorting();
  }

  // Fire-and-forget: dispatch is synchronous, so the selector already sees the change.
  private persistSorting(): void {
    this.store
      .select(selectHomeContextSettings)
      .pipe(take(1))
      .subscribe((contextSettings) => {
        this.userApi.saveContextValue(contextSettings).subscribe({
          error: (error) =>
            console.error('Failed to persist home sorting', error),
        });
      });
  }

  onViewToggle(item: ToolbarMenuItem): void {
    const scope = this.viewOptionsScope();
    if (!scope) return;

    const toggled = this.toggleViewOption(scope, item.label);

    // `withStats` is a fetch strategy, not a key term, so turning stats on has to
    // refetch explicitly; turning it off costs no request.
    //
    // Deferred by a macrotask on purpose: the workspaces query reads `withStats` out
    // of the very cache entry the flip above just patched, and `setQueryData` notifies
    // its observer through TanStack's batch while the scope `computed` only reaches it
    // on the next change-detection flush. Invalidating synchronously would refetch
    // with the pre-toggle strategy.
    if (toggled?.path === 'stats' && toggled.selected) {
      setTimeout(() => this.userWorkspaces.invalidate());
    }
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: ToolbarMenuItem[]): ToolbarMenuGroup[] {
    const groups: ToolbarMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ToolbarMenuGroup, value: unknown): void {
    const item = group.items.find((i) => i.value === value);
    if (item) {
      this.onViewToggle(item);
    }
  }

  handleSearchStringChange(query: string) {
    // Use the debounced function instead of the original search function
    if (typeof query == 'string') {
      this.filterString = query;
      this.store.dispatch(
        setHomeFilterString({ filterString: this.filterString }),
      );
    }
  }

  createNewWorkspace() {
    this.objectCreatorService.createNewWorkspace();
  }

  selectAllWorkspaces(selected: boolean) {
    if (selected) {
      this.store.dispatch(selectObjects({ objects: this.workspaces }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  trackById(index: number, workspace: any): any {
    return workspace.id;
  }
}
